import React, { useState, useEffect } from 'react';
import { View, ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import Typography from 'src/components/Text/Text';
import Button from 'src/components/Button/Button';
import InputField from 'src/components/input/input';
import CallBanner from 'src/components/CallBanner/CallBanner';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { styles } from './GaragingAddressScreen.styles';

interface Props {
  onSubmit?: (state: string) => void;
  isLoading?: boolean;
}

const GaragingAddressScreen: React.FC<Props> = ({ onSubmit, isLoading = false }) => {
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const [garagingState, setGaragingState] = useState('');

  useEffect(() => {
    trackGTMEvent('garaging-address-screen-rendered', {}, accountType);
  }, []);

  const handleSubmit = () => {
    if (!garagingState.trim()) return;
    trackGTMEvent('garaging-address-submit-button-pressed', {}, accountType);
    onSubmit?.(garagingState.trim().toUpperCase());
  };

  const handleStateChange = (value: string) => {
    // Only allow letters and limit to 2 characters
    const cleaned = value
      .replace(/[^A-Za-z]/g, '')
      .toUpperCase()
      .slice(0, 2);
    setGaragingState(cleaned);
  };

  return (
    <>
      <CallBanner />
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.content}>
          <Typography variant="h2" style={styles.title}>
            Enter Your Garaging State
          </Typography>

          <Typography variant="body1" style={styles.subtitle}>
            Please provide the state where your vehicles are primarily garaged.
          </Typography>

          <View style={styles.fieldContainer}>
            <InputField
              testID="garaging-state-input"
              variant="caption"
              label="Garaging State"
              placeholder="Enter state code (e.g., TX, CA, NY)"
              value={garagingState}
              onChange={handleStateChange}
            />
          </View>

          <View style={styles.actions}>
            <Button
              testID="submit-garaging-state"
              title={isLoading ? 'Processing...' : 'Continue'}
              type="primary"
              colorVariant={garagingState.length === 2 && !isLoading ? 'default' : 'disabled'}
              onPress={handleSubmit}
              disabled={garagingState.length !== 2 || isLoading}
              style={styles.submitButton}
            />
          </View>
        </View>
      </ScrollView>
    </>
  );
};

export default GaragingAddressScreen;
